import { useState, useMemo, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { mortgageMiniCalculatorApi } from '../api';
import { TType as TPageType } from '../../common/repositories/mortgage-broker-calculator/entities/get_offers_listing_for_newbuiling/GetOffersV1Request';
import { setRedirectUrl, setFinancePlatformEnabled, setProgramsVersion } from '../redux';
import {
  selectEntityId,
  selectFormDefaults,
  selectPageType,
  selectRealEstateType,
  selectRegionId,
  selectSkipOffersFetch,
} from '../selectors/calculator';
import { IGetOffersV1Response } from '../../common/repositories/mortgage-broker-calculator/entities/get_offers_listing_for_newbuiling/GetOffersV1Response';
import { TBenefitProgram, TObjectType, TRealEstateType } from '../types';
import { IMetaV2 } from 'shared/common/repositories/mortgage-broker-calculator/entities/get_offers_listing_for_newbuiling/MetaV2';
import { IMeta } from 'shared/common/repositories/mortgage-broker-calculator/entities/get_offers_listing_for_newbuiling/Meta';

// Изменить тип когда будем выпиливать v1
interface IParams {
  id: number;
  regionId?: number;
  objectType?: TObjectType | string;
  realEstate?: { id?: number; type?: TRealEstateType };
  type: TPageType;
  objectCost: number;
  downPayment: number;
  loanTermYears: number;
  lifeInsurance?: boolean;
  benefitProgram?: TBenefitProgram;
}

export const hasProgramsVersion = (meta: IMetaV2 | IMeta): meta is IMetaV2 => {
  return 'programsVersion' in meta;
};

export type IFetchOffersProps = Omit<IParams, 'id' | 'type' | 'objectType' | 'realEstate' | 'regionId'>;

export const useGetOffers = () => {
  const dispatch = useDispatch();
  const { objectCost, downPayment, loanTermYears, benefitProgram, objectType, lifeInsurance } =
    useSelector(selectFormDefaults);
  const entityId = useSelector(selectEntityId);
  const pageType = useSelector(selectPageType);
  const regionId = useSelector(selectRegionId);
  const realEstateType = useSelector(selectRealEstateType);
  const skipOffersFetch = useSelector(selectSkipOffersFetch);

  // Храним в state только динамические параметры, которые могут меняться через fetchOffers
  const [dynamicParams, setDynamicParams] = useState({
    objectCost,
    downPayment,
    loanTermYears,
    benefitProgram,
    lifeInsurance,
  });

  // Мемоизируем объект realEstate, чтобы избежать создания нового объекта на каждом рендере
  const realEstate = useMemo(
    () => (realEstateType ? { id: entityId, type: realEstateType } : undefined),
    [realEstateType, entityId],
  );

  // Мемоизируем финальный объект параметров
  const params = useMemo(
    () => ({
      id: entityId,
      type: pageType,
      regionId,
      objectType,
      ...dynamicParams,
      realEstate,
    }),
    [entityId, pageType, regionId, objectType, dynamicParams, realEstate],
  );

  const { data, isFetching, isError, error, refetch } = mortgageMiniCalculatorApi.endpoints.getOffers.useQuery(params, {
    skip: skipOffersFetch,
  });

  // Мемоизируем функцию fetchOffers для стабильности
  const fetchOffers = useCallback((newParams: IFetchOffersProps) => {
    setDynamicParams(prevParams => ({ ...prevParams, ...newParams }));
  }, []);

  // Обработка данных только когда data изменяется
  // Redux сам проверяет изменения и не вызывает лишних ререндеров
  useEffect(() => {
    if (data) {
      const { generalLink, financePlatformEnabled } = data.meta;

      dispatch(setRedirectUrl({ url: generalLink }));
      dispatch(setFinancePlatformEnabled(financePlatformEnabled));

      if (hasProgramsVersion(data.meta)) {
        dispatch(setProgramsVersion(data.meta.programsVersion));
      }
    }
  }, [data, dispatch]);

  return {
    data: isError ? null : (data as IGetOffersV1Response),
    isFetching,
    error: isError ? error : null,
    refetch,
    fetchOffers,
  };
};
